/**
 * Global search (#50, owner 2026-10-07): one field, always in the same place, that finds a screen, an
 * employee or a team and goes there. ⌘K, Ctrl+K or / opens it from anywhere but a text field.
 * It searches what the person may see and nothing more: screens from the menu they already have, the
 * people only with ansatte.se, the teams only with vaktplan.se. People and teams are read when the
 * field first opens, not on every page.
 */
import { get } from '@/api/client';
import type { ApiTeam, ApiUser } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Icon } from '@/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

export interface Hit {
  kind: 'side' | 'ansatt' | 'lag';
  label: string;
  sub?: string | undefined;
  to: string;
}

const norm = (s: string) => s.toLocaleLowerCase('nb').normalize('NFKD').replace(/\p{M}/gu, '');
const MAX = 6;

/** The hits for a query, grouped as screens, people, teams, at most six of each. Pure, for the tests. */
export function search(
  q: string,
  pages: { to: string; label: string }[],
  people: ApiUser[],
  teams: ApiTeam[],
): Hit[] {
  const n = norm(q.trim());
  if (!n) return [];
  const has = (s: string | null | undefined) => !!s && norm(s).includes(n);
  const side = pages
    .filter((p) => has(p.label))
    .map((p): Hit => ({ kind: 'side', label: p.label, to: p.to }));
  const ansatt = people
    .filter((u) => u.active_status !== false && has(`${u.name} ${u.lastname ?? ''}`))
    .map(
      (u): Hit => ({
        kind: 'ansatt',
        label: `${u.name} ${u.lastname ?? ''}`.trim(),
        sub: u.title ?? undefined,
        to: `/ansatte/${u.id}`,
      }),
    );
  const lag = teams
    .filter((t) => has(t.name))
    .map(
      (t): Hit => ({ kind: 'lag', label: t.name, sub: T('Vaktplan for laget'), to: `/vaktplan?lag=${t.id}` }),
    );
  return [...side.slice(0, MAX), ...ansatt.slice(0, MAX), ...lag.slice(0, MAX)];
}

const GROUP: Record<Hit['kind'], string> = { side: 'Sider', ansatt: 'Ansatte', lag: 'Lag' };

/** One dialog for the app, opened from either button (the sidebar's and the phone bar's) or a key. */
export const useSearchOpen = create<{ open: boolean; setOpen: (o: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** The field under the sidebar brand, or the magnifier in the phone bar (`compact`): both open the one dialog. */
export function SearchButton({ compact = false, top = false }: { compact?: boolean; top?: boolean }) {
  const setOpen = useSearchOpen((s) => s.setOpen);
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      className={compact ? 'bell' : top ? 'gsearch gsearch-top' : 'gsearch'}
      onClick={() => setOpen(true)}
      aria-label={T('Søk i Timefly')}
      data-testid={compact ? 'search-open-phone' : top ? 'search-open-top' : 'search-open'}
    >
      <Icon name="search" />
      {!compact && (
        <>
          <span>{top ? T('Søk i alt') : T('Søk')}</span>
          <kbd>{mac ? '⌘K' : 'Ctrl K'}</kbd>
        </>
      )}
    </button>
  );
}

/** The dialog and the keys that open it; rendered once, by the shell. */
export function GlobalSearch({ pages }: { pages: { to: string; label: string }[] }) {
  const can = useCan();
  const nav = useNavigate();
  const { open, setOpen } = useSearchOpen();
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const seePeople = can('ansatte.se');
  const seeTeams = can('vaktplan.se');
  const [asked, setAsked] = useState(false);
  const people = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const r = await get<ApiUser[] | { users: ApiUser[] }>('/users');
      return Array.isArray(r) ? r : r.users;
    },
    enabled: asked && seePeople,
    staleTime: 60_000,
  });
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => get<ApiTeam[]>('/admin/teams'),
    enabled: asked && seeTeams,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        e.target.closest('input, textarea, select, [contenteditable=true]');
      // a page that took «/» for its own search (Vaktplan's «Finn ansatt») has prevented it
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing && !e.defaultPrevented)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setOpen]);
  useEffect(() => {
    if (!open) return;
    setAsked(true);
    setQ('');
    setAt(0);
    input.current?.focus();
  }, [open]);

  const hits = useMemo(
    () => search(q, pages, seePeople ? (people.data ?? []) : [], seeTeams ? (teams.data ?? []) : []),
    [q, pages, people.data, teams.data, seePeople, seeTeams],
  );
  const go = (h: Hit | undefined) => {
    if (!h) return;
    setOpen(false);
    nav(h.to);
  };
  return (
    <>
      {open && (
        <div className="confirm" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <dialog open aria-label={T('Søk i Timefly')} className="gsdlg" data-testid="search">
            <input
              ref={input}
              className="inp"
              type="search"
              aria-label={T('Søk i Timefly')}
              placeholder={T('Søk etter side, ansatt eller lag')}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setAt(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setAt((i) => Math.min(i + 1, hits.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setAt((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter') go(hits[at]);
              }}
            />
            <ul className="gslist">
              {hits.map((h, i) => (
                <li key={`${h.kind}-${h.to}`}>
                  {(i === 0 || hits[i - 1]?.kind !== h.kind) && (
                    <small className="gsgrp">{T(GROUP[h.kind])}</small>
                  )}
                  <button
                    type="button"
                    className={i === at ? 'on' : ''}
                    onMouseEnter={() => setAt(i)}
                    onClick={() => go(h)}
                  >
                    <b>{h.label}</b>
                    {h.sub && <span>{h.sub}</span>}
                  </button>
                </li>
              ))}
            </ul>
            {q.trim() && !hits.length && <p className="gsnone">{T('Ingen treff.')}</p>}
          </dialog>
        </div>
      )}
    </>
  );
}
